import * as React from 'react'

import useI18n from '@store/i18n/useI18n'
import styled from '@theme/styled-components'

import Select from '@components/select/select'
import Button from '@components/button/button'
import CsvPreview from '@components/csvPreview/CsvPreview'
import Swal from 'sweetalert2'

import apis from '@app/api/api'
import downloadCsv from '@utils/CSVutils'

interface Props {
  user_type_options: Option[]
}

const TimeOptions: Option[] = [
  {
    value: 'day',
    label: 'Depuis 1 jour',
    param: 1,
  },
  {
    value: 'week',
    label: 'Depuis 1 semaine',
    param: 7,
  },
  {
    value: 'month',
    label: 'Depuis 1 mois',
    param: 31,
  },
  {
    value: 'all',
    label: 'Tout',
    param: 3650,
  },
]

const UserExport = ({ user_type_options }: Props) => {
  const i18n = useI18n()

  const [userType, setUserType] = React.useState<Option>()
  const [exportPeriod, setExportPeriod] = React.useState<Option>()

  const [fileContent, setFileContent] = React.useState<any>()
  const [loading, setLoading] = React.useState<boolean>(false)

  const exportUsers = () => {
    if (userType && exportPeriod) {
      const type = userType.value as UserType
      const daysSinceCreation = exportPeriod.param || 0

      setLoading(true)
      apis.admin.export
        .users({ type, daysSinceCreation })
        .then((res) => {
          if (!res) {
            Swal.fire({
              title: i18n.t('components.adminData.noexport.title'),
              text: i18n.t('components.adminData.noexport.text'),
              icon: 'info',
              showCancelButton: false,
              confirmButtonColor: '#3A8641',
              confirmButtonText: i18n.t('components.adminData.noexport.confirm'),
            })
          } else {
            setFileContent(res)
            downloadCsv(
              res,
              type === 'EXTERNAL'
                ? 'utilisateurs_externes.csv'
                : type === 'INTERNAL'
                ? 'utilisateurs_internes.csv'
                : 'utilisateurs_ckcc.csv'
            )
          }
          setLoading(false)
        })
        .catch(() => {
          Swal.fire({
            title: i18n.t('components.adminData.errorexport.title'),
            text: i18n.t('components.adminData.errorexport.text'),
            icon: 'error',
            showCancelButton: false,
            confirmButtonColor: '#3A8641',
            confirmButtonText: i18n.t('components.adminData.errorexport.confirm'),
          })
          setLoading(false)
        })
    }
  }

  return (
    <>
      <SectionTitleContainer>{i18n.t('components.adminData.sectionTitles.userExport')}</SectionTitleContainer>
      <RowContainer>
        <FormItemContainer>
          <SelectorContainer>
            <SelectorTitle>{i18n.t('components.adminData.type')}</SelectorTitle>
            <Select
              selected={userType}
              options={user_type_options}
              setSelected={setUserType}
              isClearable
              placeholder={i18n.t('components.adminData.placeholders.type')}
              noMargin
            />
          </SelectorContainer>
        </FormItemContainer>
        <FormItemContainer>
          <SelectorContainer>
            <SelectorTitle>{i18n.t('components.adminData.creationDate')}</SelectorTitle>
            <Select
              selected={exportPeriod}
              options={TimeOptions}
              setSelected={setExportPeriod}
              isClearable
              placeholder={i18n.t('components.adminData.placeholders.creationDate')}
              noMargin
            />
          </SelectorContainer>
        </FormItemContainer>
      </RowContainer>
      <ButtonContainer>
        <Button
          title={i18n.t('components.adminData.export')}
          onClick={exportUsers}
          type="action"
          status={loading ? 'loading' : !!userType && !!exportPeriod ? 'active' : 'disabled'}
        />
      </ButtonContainer>
      {fileContent && <CsvPreview csv={fileContent} />}
    </>
  )
}

export default UserExport

const SectionTitleContainer = styled('div')`
  ${(props) => props.theme.fonts.sectionTitle};
  margin: 10px;
  margin-top: 0;
`
const RowContainer = styled('div')`
  display: flex;
  align-items: center;
  margin: 10px;
  margin-top: 0;
`
const FormItemContainer = styled('div')<{ center?: boolean }>`
  display: flex;
  ${(props) => props.center && 'align-items: center'}
  flex: 1;
  margin: 10px;
  margin-top: 0;
`
const SelectorContainer = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;

  margin: 5px;
`
const SelectorTitle = styled('div')`
  ${(props) => props.theme.fonts.selectorTitle};
`
const ButtonContainer = styled('div')`
  margin-top: 25px;
  margin-bottom: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
`
